<template>
  <tr
    class="bg-white hover:bg-gray-50 transition-colors duration-150 ease-in-out text-right"
    @click="open = !open"
  >
    <td class="flex pl-3 py-5 items-center space-x-1">
      <button
        type="button"
        class="btn-icon btn-icon-default"
        data-test="open-dayworks"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 transition-all duration-200"
          :class="[{'rotate-90': open}]">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
      </button>

      <span>Dayworks</span>
    </td>

    <td class="pr-3">
      {{ australianCurrency(totalCosts.calculated) }}
    </td>

    <td v-if="claim.boq_type === 'subcontractor_schedule_of_rates'" />

    <td class="bg-green-200/50">
      {{ australianCurrency(totalCosts.currentClaim) }}
    </td>

    <td class="bg-green-200/50" />

    <td class="bg-green-200/50" />

    <td class="bg-green-200/50">
      {{ australianCurrency(totalCosts.previous) }}
    </td>

    <td class="bg-green-200/50 pr-3">
      {{ australianCurrency(totalCosts.cumulative) }}
    </td>

    <td class="bg-blue-100/50">
      <span v-if="showPercentages">
        {{ totalCosts.calculated > 0 ? australianNumber((totalCosts.currentClaim / totalCosts.calculated) * 100) : 0 }}%
      </span>
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    />

    <td
      v-if="showPercentages"
      class="bg-blue-100/50"
    >
      {{ totalCosts.calculated > 0 ? australianNumber((totalCosts.previous / totalCosts.calculated) * 100) : 0 }}%
    </td>

    <td
      v-if="showPercentages"
      class="bg-blue-100/50 pr-3"
    >
      {{ totalCosts.calculated > 0 ? australianNumber((totalCosts.cumulative / totalCosts.calculated) * 100) : 0 }}%
    </td>

    <td class="pr-3">
      {{ australianCurrency(totalCosts.calculated - totalCosts.cumulative) }}
    </td>
  </tr>

  <template v-if="open">
    <tr
      v-if="labours.length"
      class="bg-gray-100 text-left"
    >
      <td
        class="pl-10 py-2 font-semibold text-sm text-gray-700"
        colspan="100%"
      >
        Labour
      </td>
    </tr>

    <DayworksCostRow
      v-for="labour in labours"
      :key="`labour-${labour.id}`"
      :type="DayworksType.labour"
      :labour="labour"
      :claim="claim"
      :show-percentages="showPercentages"
    />

    <tr
      v-if="plants.length"
      class="bg-gray-100 text-left"
    >
      <td
        class="pl-10 py-2 font-semibold text-sm text-gray-700"
        colspan="100%"
      >
        Plant
      </td>
    </tr>

    <DayworksCostRow
      v-for="plant in plants"
      :key="`plant-${plant.id}`"
      :type="DayworksType.plant"
      :plant="plant"
      :claim="claim"
      :show-percentages="showPercentages"
    />
  </template>
</template>

<script setup lang="ts">
import { australianCurrency, australianNumber } from '@/helpers';
import Claim from '@/models/Claim';
import { DayworksType } from '@/models/DayworksCost';
import { useLaboursStore } from '@/store/labours';
import { usePlantsStore } from '@/store/plants';
import { computed, ref } from 'vue';
import DayworksCostRow from './DayworksCostRow.vue';

const props = defineProps<{
  claim: Claim;
  showPercentages?: boolean;
}>();

const open = ref(false);
const laboursStore = useLaboursStore();
const plantsStore = usePlantsStore();

const labours = computed(() => {
  return laboursStore.models
    .where('$claim_id', props.claim.id)
    .orderBy('first_name')
    .orderBy('last_name')
    .with('dayworks_costs', (query) => {
      query.where('claim_id', props.claim.id);
    })
    .get();
});

const plants = computed(() => {
  return plantsStore.models
    .where('$claim_id', props.claim.id)
    .orderBy('fleet')
    .with('dayworks_costs', (query) => {
      query.where('claim_id', props.claim.id);
    })
    .get();
});

const totalCosts = computed(() => {
  const totals = {
    calculated: 0,
    currentClaim: 0,
    previous: 0,
    cumulative: 0,
  };

  labours.value.forEach((labour) => {
    const cost = labour.dayworks_costs[0];

    totals.calculated += labour.calculatedAmount || 0;
    totals.currentClaim += cost?.current_claim || 0;
    totals.previous += labour.$previous || 0;
  });

  plants.value.forEach((plant) => {
    const cost = plant.dayworks_costs[0];

    totals.calculated += plant.calculatedAmount || 0;
    totals.currentClaim += cost?.current_claim || 0;
    totals.previous += plant.$previous || 0;
  });

  totals.cumulative = totals.previous + totals.currentClaim;

  return totals;
});
</script>
